import { useAppSelector } from "app/hooks"
import { Button } from "components/ui/Button"
import { CheckIcon } from "components/icons/Icons"
import Loading, { Sizes } from "components/ui/Loader"
import { AlertTriangle, FileText } from "lucide-react"
import { FC } from "react"
import { selectIsCartLockedForFamily, selectIsPricingBusy } from "../../slices/pricing/selectors"
import BadgeTooltip from "components/ui/BadgeTooltip"
import { NsKey } from "enums/NsKey"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { ContractFunnel } from "../../types/interfaces"
import { useFunnelCardFooter } from "./hooks/useFunnelCardFooter"
import { documentLabels } from "shared/constants/documentLabels"

interface FunnelCardFooterProps {
  contractFunnel: ContractFunnel
}

export const FunnelCardFooter: FC<FunnelCardFooterProps> = ({ contractFunnel }) => {
  const { isInShoppingCart, toggleCart, onDownload, isDownloading, contractualDocuments, isCollectiveContractDraft, isIneligible, generateDevisOnTheFly, isGeneratingDevis } = useFunnelCardFooter(contractFunnel);
  // Re-tarif global en cours : loader directement sur le prix + actions bloquées (prix périmé).
  const isPricingBusy = useAppSelector(selectIsPricingBusy)
  // Mixed page: the cart holds either formulas or comparable products, never both (H8).
  const isCartLocked = useAppSelector(selectIsCartLockedForFamily(!!contractFunnel.product?.isFixedFormula)) && !isInShoppingCart
  const { t } = useDynamicTranslation([NsKey.Pricing, NsKey.Document])

  const addButton = (
    <Button
      color="secondary"
      className={`w-full ${isInShoppingCart ? "opacity-80" : ""}`}
      onClick={toggleCart}
      disabled={isPricingBusy || isCartLocked}
    >
      {isInShoppingCart ? "Ajouté" : "Ajouter à la sélection"}
      {isInShoppingCart && <CheckIcon />}
    </Button>
  )

  return (
    <div className="flex items-center justify-center flex-col gap-4 py-4 px-6 flex-none">
      {isIneligible ? (
        // Produit inéligible (contrainte produit) ou erreur de tarif : pas de prix ni
        // d'action d'ajout — l'indicateur « Produit non éligible » remplace le bouton.
        // Le motif détaillé est en bandeau pleine largeur au bas de la cartouche.
        <div className="flex min-w-[180px] flex-col items-center gap-2 text-center">
          <span className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-red-100 px-3 py-2 text-sm font-semibold text-red-800">
            <AlertTriangle className="h-4 w-4" />
            Produit non éligible
          </span>
        </div>
      ) : (
        <>
          {/* Pendant un re-tarif : spinner en overlay, le prix reste rendu en invisible → la card ne se redimensionne pas. */}
          <div className="relative flex flex-col items-center justify-center">
            {isPricingBusy && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loading loading size={Sizes.sm} />
              </div>
            )}
            <div className={isPricingBusy ? "invisible" : ""}>
              <p className="text-md 2xl:text-2xl font-extrabold text-center">
                {contractFunnel.pricing.monthlyPriceIncludingTax?.toFixed(2)} €
                <span className="text-xs 2xl:text-sm"> TTC hors frais</span>
                <span className="text-sm 2xl:text-xl text-gray-400"> / mois</span>
              </p>
              <p className="flex justify-center text-sm 2xl:text-xl gap-1">
                soit {contractFunnel.pricing.clientPrice?.toFixed(2)} €
                <span className="text-sm 2xl:text-xl text-gray-400"> / an</span>
              </p>
            </div>
          </div>

          {isCollectiveContractDraft ? (
            // Contrat collectif non signé : ajout au panier interdit, devis à la volée uniquement.
            <div className="w-full flex flex-col items-center gap-1">
              <Button
                color="secondary"
                className="w-full"
                onClick={generateDevisOnTheFly}
                disabled={isGeneratingDevis || isPricingBusy}
              >
                {isGeneratingDevis ? "Génération..." : "Générer un devis"}
              </Button>
              <p className="text-xs text-gray-400 text-center">
                Contrat collectif non signé — devis uniquement
              </p>
            </div>
          ) : (
            // Tunnel individuel ou contrat collectif signé : ajout au panier + devis à la volée.
            <div className="w-full flex flex-col items-center gap-2">
              {isCartLocked
                ? <BadgeTooltip content={t("PRICING.FORMULAS.CART_LOCKED")} className="block w-full">{addButton}</BadgeTooltip>
                : addButton}
              <Button
                color="primary"
                outline
                className="w-full"
                onClick={generateDevisOnTheFly}
                disabled={isGeneratingDevis || isPricingBusy}
              >
                {isGeneratingDevis ? "Génération..." : "Générer un devis"}
              </Button>
            </div>
          )}
        </>
      )}
      {contractualDocuments.length > 0 && (
        <div>
          {contractualDocuments.map((document) => {
            const isThisDownloading = isDownloading === document.storedFileId
            const labelKey = documentLabels[document.fileType]
            return (
              <div
                key={document.storedFileId}
                className={`flex items-center gap-2 ${isThisDownloading ? 'opacity-50' : 'cursor-pointer'}`}
                onClick={() => !isDownloading && onDownload(document.storedFileId)}
              >
                <FileText className="w-4 h-4 text-primary" />
                <p className="text-primary hover:underline">
                  {isThisDownloading ? 'Téléchargement...' : document.label || (labelKey ? t(labelKey) : '')}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
